import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowUpRight,
  ArrowLeft,
  Image as ImageIcon,
  GitBranch,
  Layers,
  Workflow,
} from "lucide-react";
import { Navbar } from "../components/Navbar";
import { useTheme } from "../hooks/useTheme";
import { useLenis } from "../hooks/useLenis";

const reveal = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

const Section = ({ num, title, children, id }) => (
  <motion.section
    id={id}
    variants={reveal}
    initial="hidden"
    whileInView="show"
    viewport={{ once: true, margin: "-80px" }}
    className="border-t border-[var(--border)] py-14 sm:py-20"
    data-testid={`cs-section-${id}`}
  >
    <div className="grid grid-cols-12 gap-6">
      <div className="col-span-12 lg:col-span-3">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[var(--accent)]">
          {num}
        </p>
        <h2 className="mt-2 font-display text-2xl font-black uppercase leading-none tracking-tight sm:text-3xl">
          {title}
        </h2>
      </div>
      <div className="col-span-12 lg:col-span-9">{children}</div>
    </div>
  </motion.section>
);

const Placeholder = ({ label }) => (
  <div className="flex flex-col items-center justify-center gap-2 rounded-sm border border-dashed border-[var(--border)] bg-[var(--surface)] px-6 py-14 text-center">
    <ImageIcon size={22} className="text-[var(--text-2)]" />
    <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--text-2)]">{label}</p>
    <p className="font-mono text-[10px] text-[var(--text-2)]/70">Screenshot / diagram to be added</p>
  </div>
);

const P = ({ children }) => (
  <p className="mb-4 max-w-3xl text-base leading-relaxed text-[var(--text-2)] sm:text-lg">{children}</p>
);

const Bullets = ({ items }) => (
  <ul className="mb-4 flex max-w-3xl flex-col gap-2.5">
    {items.map((t, i) => (
      <li key={i} className="flex gap-3 text-base leading-relaxed text-[var(--text-2)]">
        <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--accent)]" />
        <span>{t}</span>
      </li>
    ))}
  </ul>
);

const content = {
  "kassam-trading": {
    title: "Kassam Trading Company",
    subtitle: "Digital Transformation & Trade Management Platform",
    orgs: "Kassam Trading Company (KTC) · NBMT Trading",
    roles: ["Digital Transformation Lead", "Product Designer & Developer", "E-commerce & Marketing Strategist"],
    live: "https://app.kassamtradingcompany.com/",
  },
};

export default function CaseStudy() {
  const { slug } = useParams();
  const { dark, toggle } = useTheme();
  useLenis(true);
  const data = content[slug];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!data) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-[var(--bg)] text-[var(--text)]">
        <p className="font-display text-2xl font-bold">Case study not found.</p>
        <Link to="/" className="link-underline font-mono text-xs uppercase tracking-widest">
          ← Back to portfolio
        </Link>
      </div>
    );
  }

  const features = [
    "Customer & lead management",
    "Order creation & lifecycle tracking",
    "Shipment & delivery visibility",
    "Product & company records",
    "Trade document organization",
    "Outreach campaign creation",
    "Multiple email sender accounts",
    "Communication & activity tracking",
    "Dashboard reporting",
    "Role-based operational workflows",
  ];

  const stack = [
    "React", "TypeScript", "Supabase", "PostgreSQL", "Supabase Edge Functions",
    "GitHub / PR workflows", "Vercel", "Zoho Mail API & OAuth", "Brevo",
    "Amazon Seller Central", "Instagram", "LinkedIn",
  ];

  const skills = [
    "Product & workflow design", "Full-stack development", "Database & backend integration",
    "API & OAuth integration", "Production debugging", "Secure credential management",
    "Business process analysis", "Digital transformation strategy", "CRM development",
    "E-commerce planning", "Content & social strategy", "B2B lead generation",
    "Version control", "Quality assurance", "Cross-functional project management",
  ];

  return (
    <div className="relative min-h-screen bg-[var(--bg)] text-[var(--text)]">
      <div className="noise-overlay" />
      <Navbar dark={dark} toggleTheme={toggle} simple />

      {/* HERO */}
      <header className="px-5 pb-10 pt-32 sm:px-10">
        <div className="mx-auto max-w-[1400px]">
          <Link
            to="/"
            data-testid="cs-back"
            className="mb-10 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-[var(--text-2)] transition-colors hover:text-[var(--text)]"
          >
            <ArrowLeft size={14} /> Back to portfolio
          </Link>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[var(--accent)]">
            Case Study · {data.orgs}
          </p>
          <h1 className="mt-5 font-display font-black uppercase leading-[0.85] tracking-tighter" style={{ fontSize: "clamp(2.5rem, 8vw, 7rem)" }}>
            {data.title}
          </h1>
          <p className="mt-4 max-w-3xl font-display text-xl font-medium tracking-tight text-[var(--text-2)] sm:text-2xl">
            {data.subtitle}
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            {data.roles.map((r) => (
              <span key={r} className="rounded-full border border-[var(--border)] px-4 py-2 font-mono text-[10px] uppercase tracking-wider text-[var(--text-2)]">
                {r}
              </span>
            ))}
          </div>
          <a
            href={data.live}
            target="_blank"
            rel="noreferrer"
            data-testid="cs-live-link"
            className="mt-8 inline-flex items-center gap-2 rounded-sm bg-[var(--text)] px-6 py-3.5 font-mono text-xs uppercase tracking-widest text-[var(--bg)] transition-transform duration-300 hover:-translate-y-0.5"
          >
            Visit live app <ArrowUpRight size={14} />
          </a>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-5 sm:px-10">
        <div className="mt-6 overflow-hidden rounded-sm border border-[var(--border)]">
          <img src="/case-study/ktc-hero.jpg" alt="KTC Trade Manager dashboard overview" className="w-full" loading="lazy" />
        </div>

        <Section num="01" id="overview" title="Overview">
          <P>
            Led an end-to-end digital transformation for Kassam Trading Company, a rice and spices
            export-import business, combining business development, digital marketing, e-commerce
            planning, workflow design, and the development of a centralized trade-management application.
          </P>
          <P>
            Also helped establish NBMT Trading as a sister company, creating additional operational
            flexibility and new opportunities for international market expansion. This was a real business
            transformation initiative, not a standalone website or social-media effort.
          </P>
        </Section>

        <Section num="02" id="problem" title="The Business Problem">
          <P>
            KTC relied on traditional, fragmented processes to manage orders, customers, documents,
            communication, and sales outreach. The business needed a modern operational backbone:
          </P>
          <Bullets items={[
            "A more professional digital presence",
            "End-to-end visibility into the order lifecycle",
            "Centralized customer and trade records",
            "Improved outreach and email management",
            "Modern sales channels to grow order inquiries",
            "A scalable structure spanning KTC and NBMT Trading",
          ]} />
        </Section>

        <Section num="03" id="role" title="My Role">
          <Bullets items={[
            "Business Development & Digital Transformation Lead",
            "Product Designer & Application Developer",
            "Digital Marketing & E-commerce Strategist",
          ]} />
        </Section>

        <Section num="04" id="research" title="Research & Planning">
          <P>Grounded the initiative in market and operational research before building:</P>
          <Bullets items={[
            "Structured NBMT Trading as a strategic sister company and defined its positioning",
            "Developed digital growth strategies for both KTC and NBMT Trading",
            "Established a consistent, professional brand direction",
            "Researched competitors, export markets, customer segments, and digital sales opportunities",
            "Planned scalable business processes for international expansion",
          ]} />
        </Section>

        <Section num="05" id="journey" title="Customer Journey">
          <P>
            Defined the complete KTC customer and order journey, from the moment an order is confirmed
            through fulfillment and closure, and documented it in the “KTC Order Tracker: User Journey.”
            Real business operations were translated into application requirements, statuses, and workflows.
          </P>
          <Bullets items={[
            "Customer onboarding and order creation",
            "Document collection and payment tracking",
            "Shipping updates and delivery",
            "Order closure and record-keeping",
          ]} />
          <div className="mt-6 overflow-hidden rounded-sm border border-[var(--border)]">
            <img
              src="https://static.prod-images.emergentagent.com/jobs/6033b893-3fd9-46ac-95cf-e6d4b6e9ba04/images/a3070e27af3af1ec984e1c625e20912391ba1b88e043edb76a893bdcae51ffea.jpeg"
              alt="KTC Order Tracker customer journey diagram"
              className="w-full"
            />
          </div>
        </Section>

        <Section num="06" id="features" title="Platform Features">
          <div className="mb-6 flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-[var(--text-2)]">
            <Layers size={14} /> KTC Trade Manager
          </div>
          <div className="grid grid-cols-1 gap-px overflow-hidden rounded-sm border border-[var(--border)] bg-[var(--border)] sm:grid-cols-2">
            {features.map((f) => (
              <div key={f} className="bg-[var(--surface)] px-5 py-4 font-mono text-xs uppercase tracking-wider text-[var(--text)]">
                {f}
              </div>
            ))}
          </div>
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="overflow-hidden rounded-sm border border-[var(--border)]">
              <img src="/case-study/ktc-orders.jpg" alt="KTC order tracker screen" className="w-full" loading="lazy" />
            </div>
            <div className="overflow-hidden rounded-sm border border-[var(--border)]">
              <img src="/case-study/ktc-dashboard.jpg" alt="KTC dashboard and reporting" className="w-full" loading="lazy" />
            </div>
          </div>
        </Section>

        <Section num="07" id="architecture" title="Technical Architecture">
          <P>
            Built and refined the platform on a modern full-stack foundation, reviewing and simplifying a
            complex multi-page codebase, aligning it to the approved user journey, and improving overall
            maintainability.
          </P>
          <div className="my-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
            {[
              { icon: Layers, label: "Frontend", detail: "React · TypeScript · Vercel" },
              { icon: Workflow, label: "Backend", detail: "Supabase · PostgreSQL · Edge Functions" },
              { icon: GitBranch, label: "Delivery", detail: "GitHub · PR workflow · Env secrets" },
            ].map((b) => (
              <div key={b.label} className="border border-[var(--border)] bg-[var(--surface)] p-5">
                <b.icon size={18} className="text-[var(--accent)]" />
                <p className="mt-3 font-display text-lg font-bold tracking-tight">{b.label}</p>
                <p className="mt-1 font-mono text-[11px] uppercase tracking-wider text-[var(--text-2)]">{b.detail}</p>
              </div>
            ))}
          </div>
          <Bullets items={[
            "Used Supabase for database, authentication, secrets, and Edge Functions",
            "Connected frontend workflows to backend services",
            "Managed production configuration securely through environment secrets",
            "Validated and deployed function updates while keeping GitHub and production in sync",
            "Isolated changes into focused branches and pull requests to protect unrelated work",
          ]} />
          <div className="mt-6 overflow-hidden rounded-sm border border-[var(--border)]">
            <img
              src="https://static.prod-images.emergentagent.com/jobs/6033b893-3fd9-46ac-95cf-e6d4b6e9ba04/images/78a0befe8c1982936fd1bd424f9be7ebce23a2d490250c3e273cc4d116253390.jpeg"
              alt="KTC Trade Manager system architecture diagram"
              className="w-full"
            />
          </div>
        </Section>

        <Section num="08" id="marketing" title="Digital Marketing & E-commerce">
          <P><strong className="text-[var(--text)]">Social & digital presence.</strong> Established and managed professional Instagram and LinkedIn accounts with content pillars, posting schedules, product showcases, and brand storytelling, strengthening credibility and B2B lead generation.</P>
          <P><strong className="text-[var(--text)]">E-commerce strategy.</strong> Developed an Amazon Marketplace expansion plan covering product positioning, keywords, logistics, and fulfillment, with optimized listings for rice and spice products and future paths through Alibaba, Global Sources, email, and paid advertising.</P>
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="overflow-hidden rounded-sm border border-[var(--border)]">
              <img src="/case-study/ktc-social.jpg" alt="Kassam Trading Company social / company page" className="w-full" loading="lazy" />
            </div>
            <div className="overflow-hidden rounded-sm border border-[var(--border)]">
              <img src="/case-study/ktc-amazon.jpg" alt="Amazon Seller Central global marketplace expansion" className="w-full" loading="lazy" />
            </div>
          </div>
        </Section>

        <Section num="09" id="challenges" title="Challenges & Solutions">
          <P><strong className="text-[var(--text)]">Multi-mailbox Zoho integration.</strong> Integrated multiple KTC and NBMT mailboxes (Bushra, Sultan, trading@, export@, nazima.piracha@nbmttrading.com) alongside the existing Brevo route into the CRM's Compose Outreach feature, configuring OAuth self-client access, exchanging authorization codes, storing credentials securely in Supabase, and building mailbox-specific credential routing without disrupting existing senders.</P>
          <P><strong className="text-[var(--text)]">Production debugging.</strong> Diagnosed an INVALID_INPUT error affecting emails from NBMT mailboxes. After ruling out recipient addresses, traced it to the shared Zoho PDF attachment payload:</P>
          <Bullets items={[
            "Updated the upload request to Zoho's required multipart format",
            "Limited attachment metadata to storeName, attachmentName, and attachmentPath",
            "Added validation for incomplete attachment uploads and normalized email addresses",
            "Ran lint and production-build checks, shipped via reviewed GitHub PRs, and verified the corrected Edge Function in production",
          ]} />
          <div className="mt-6 overflow-hidden rounded-sm border border-[var(--border)]">
            <img src="/case-study/ktc-github.jpg" alt="KTC Trade Manager GitHub repository" className="w-full" loading="lazy" />
          </div>
        </Section>

        <Section num="10" id="outcomes" title="Outcomes & Impact">
          <Bullets items={[
            "Transformed fragmented processes into a centralized digital workflow",
            "Created a working foundation for tracking customers, orders, communications, shipments, and documents",
            "Connected multiple company mailboxes into one outreach system",
            "Improved operational visibility and consistency",
            "Established modern social, e-commerce, and B2B sales channels",
            "Built scalable infrastructure for future automation, reporting, and growth",
          ]} />
          <p className="mt-6 mb-3 font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--text-2)]">Verified results (to be confirmed)</p>
          <div className="grid grid-cols-2 gap-px overflow-hidden rounded-sm border border-[var(--border)] bg-[var(--border)] sm:grid-cols-4">
            {[
              "Customer / company records",
              "Orders tracked",
              "Email accounts integrated",
              "Increase in inquiries / leads",
              "Instagram audience growth",
              "LinkedIn reach / connections",
              "Amazon listings prepared",
              "Reduction in manual time",
            ].map((m) => (
              <div key={m} className="bg-[var(--surface)] px-4 py-5">
                <p className="font-display text-2xl font-black tracking-tight text-[var(--accent)]">[ ]</p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-wider text-[var(--text-2)]">{m}</p>
              </div>
            ))}
          </div>
        </Section>

        <Section num="11" id="skills" title="Skills & Technologies">
          <p className="mb-3 font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--text-2)]">Skills</p>
          <div className="mb-8 flex flex-wrap gap-2">
            {skills.map((s) => (
              <span key={s} className="rounded-full border border-[var(--border)] px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-[var(--text-2)]">{s}</span>
            ))}
          </div>
          <p className="mb-3 font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--text-2)]">Technologies & Tools</p>
          <div className="flex flex-wrap gap-2">
            {stack.map((s) => (
              <span key={s} className="rounded-full bg-[var(--surface)] px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-[var(--text)] ring-1 ring-[var(--border)]">{s}</span>
            ))}
          </div>
        </Section>

        <Section num="12" id="future" title="Future Improvements">
          <Bullets items={[
            "Automated reporting and analytics dashboards",
            "Workflow automation for repetitive order and outreach steps",
            "Deeper e-commerce integration across marketplaces",
            "Expanded role-based access and audit trails",
          ]} />
        </Section>

        <div className="border-t border-[var(--border)] py-16">
          <Link
            to="/"
            className="inline-flex items-center gap-2 font-display text-2xl font-bold tracking-tight"
          >
            <ArrowLeft size={22} className="text-[var(--accent)]" />
            <span className="link-underline">Back to portfolio</span>
          </Link>
        </div>
      </main>
    </div>
  );
}
